/**
 * Shared shift types for API contracts between frontend and backend
 */
import { z } from "zod";
import { CreatePayConfigurationInputSchema } from './payConfiguration.js';
import { TimecardStatusSchema, BreakTypeSchema, TimecardResponseSchema } from "./timecard.js";
// Zod schema for shift status
const ShiftStatusSchema = z.enum(["DRAFT", "PUBLISHED"]);
// Tip pooling enum
export const TipPoolingTypeSchema = z.enum(["POOLED", "INDIVIDUAL", "NOT_APPLICABLE"]);
// Violation type for labor law violations
export const ViolationSchema = z.object({
    id: z.string(),
    message: z.string(),
});
// Break input for inline shift creation (omits shiftId and jobRoleFamilyId)
export const CreateBreakForShiftInputSchema = z.object({
    shiftLengthThresholdMinutes: z.number().int().min(0),
    startOffsetMinutes: z.number().int().min(0),
    breakLengthMinutes: z.number().int().positive(),
    isPaid: z.boolean(),
    description: z.string().max(50).optional(),
});
// Create shift input schema
// Note: companyId comes from JWT context, not request body
export const CreateShiftInputSchema = z.object({
    startTime: z.iso.datetime(),
    endTime: z.iso.datetime(),
    jobRoleIds: z.array(z.uuid()).default([]),
    jobRoleFamilyId: z.uuid(),
    locationId: z.uuid(),
    employeeId: z.uuid().nullable().optional(),
    tipPooling: TipPoolingTypeSchema, // Required field
    notes: z.string().max(1000).default(""),
    payConfiguration: CreatePayConfigurationInputSchema.optional(),
    breaks: z.array(CreateBreakForShiftInputSchema).optional(),
}).refine((data) => new Date(data.endTime) > new Date(data.startTime), { message: "End time must be after start time", path: ["endTime"] });
// Update shift input schema
export const UpdateShiftInputSchema = z.object({
    startTime: z.iso.datetime().optional(),
    endTime: z.iso.datetime().optional(),
    jobRoleIds: z.array(z.uuid()).optional(),
    jobRoleFamilyId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    employeeId: z.uuid().nullable().optional(),
    tipPooling: TipPoolingTypeSchema.optional(),
    notes: z.string().max(1000).optional(),
    seekingCoverage: z.boolean().optional(),
    isUnplanned: z.boolean().optional(),
    allowEarlyClockIn: z.boolean().optional(),
}).refine((data) => Object.keys(data).length > 0, { message: "At least one field must be provided for update" });
// ===== QUERY PARAMETER SCHEMAS =====
/**
 * Get shifts query parameters
 * Note: companyId comes from JWT context, not query params
 */
export const GetShiftsQuerySchema = z.object({
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().optional(),
}).refine((data) => {
    if (data.startDate && data.endDate) {
        return new Date(data.endDate) > new Date(data.startDate);
    }
    return true;
}, {
    message: "End date must be after start date",
    path: ["endDate"],
});
/**
 * My shifts query parameters
 * For GET /api/shifts/my - uses JWT userId to find employee
 * Note: companyId comes from JWT context, not query params
 */
export const MyShiftsQuerySchema = z.object({
    startDate: z.iso.datetime().optional(),
    endDate: z.iso.datetime().optional(),
}).refine((data) => {
    if (data.startDate && data.endDate) {
        return new Date(data.endDate) > new Date(data.startDate);
    }
    return true;
}, {
    message: "End date must be after start date",
    path: ["endDate"],
});
// ===== ASSIGNMENT SCHEMAS =====
/**
 * Assign employee to shift body schema
 * Used for: PATCH /shifts/:id/assign
 */
export const AssignEmployeeInputSchema = z.object({
    employeeId: z.uuid(),
});
// ===== BATCH PUBLISH SCHEMAS =====
/**
 * Batch create shift input (frontend format)
 * Frontend sends storeLocationId, backend expects locationId
 * Validates that at least one location field is provided
 */
const BatchCreateShiftSchema = z.object({
    startTime: z.iso.datetime(),
    endTime: z.iso.datetime(),
    jobRoleIds: z.array(z.uuid()).default([]),
    jobRoleFamilyId: z.uuid(),
    locationId: z.uuid().optional(),
    storeLocationId: z.uuid().optional(), // Legacy frontend field
    employeeId: z.uuid().optional(),
    payConfiguration: CreatePayConfigurationInputSchema.optional(),
    tipPooling: TipPoolingTypeSchema.optional(),
    notes: z.string().max(1000).optional(),
}).refine((data) => data.locationId || data.storeLocationId, {
    message: "Either locationId or storeLocationId must be provided",
    path: ["locationId"],
});
/**
 * Batch update shift input
 * Partial updates with optional location normalization
 */
const BatchUpdateShiftSchema = z.object({
    id: z.uuid(),
    startTime: z.iso.datetime().optional(),
    endTime: z.iso.datetime().optional(),
    jobRoleIds: z.array(z.uuid()).optional(),
    jobRoleFamilyId: z.uuid().optional(),
    locationId: z.uuid().optional(),
    storeLocationId: z.uuid().optional(), // Legacy
    employeeId: z.uuid().nullable().optional(),
    tipPooling: TipPoolingTypeSchema.optional(),
    notes: z.string().max(1000).optional(),
});
/**
 * Batch assignment input
 * null employeeId = unassign
 */
const BatchAssignmentSchema = z.object({
    shiftId: z.uuid(),
    employeeId: z.uuid().nullable(),
});
/**
 * Batch publish request body schema
 * Validates entire batch-publish operation
 * Requires at least one operation type to be provided
 *
 * Publish event tracking (optional):
 * - locationId: The location being published for
 * - warningsSnapshot: The warnings state at publish time (JSON blob)
 * - overrideIds: IDs of warning overrides that were applied during this publish
 */
export const BatchPublishInputSchema = z.object({
    creates: z.array(BatchCreateShiftSchema).optional(),
    updates: z.array(BatchUpdateShiftSchema).optional(),
    deletes: z.array(z.uuid()).optional(),
    assignments: z.array(BatchAssignmentSchema).optional(),
    // Publish event tracking
    locationId: z.uuid(), // Required - always publishing to a specific location
    warningsSnapshot: z.record(z.string(), z.unknown()).optional(),
    overrideIds: z.array(z.uuid()).optional(),
    // When true, skip sending notifications to affected employees
    skipNotifications: z.boolean().optional().default(false),
}).refine((data) => {
    // At least one operation must be provided
    const hasOperations = (data.creates && data.creates.length > 0) ||
        (data.updates && data.updates.length > 0) ||
        (data.deletes && data.deletes.length > 0) ||
        (data.assignments && data.assignments.length > 0);
    return hasOperations;
}, {
    message: "At least one operation (creates, updates, deletes, or assignments) must be provided",
});
/**
 * Zod schema for nested job role in shift response
 */
const JobRoleInShiftSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    jobLevelId: z.uuid(),
    jobRoleFamilyId: z.uuid(),
    companyId: z.uuid(),
    employmentId: z.uuid().nullable(),
});
/**
 * Zod schema for nested location in shift response
 */
const LocationInShiftSchema = z.object({
    id: z.uuid(),
    addressLine1: z.string(),
    addressLine2: z.string().nullable(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string(),
    country: z.string(),
    phone: z.string().nullable(),
    latitude: z.number().nullable(),
    longitude: z.number().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    companyId: z.uuid(),
    sourceSystem: z.string().nullable(),
    sourceSystemId: z.string().nullable(),
    timezone: z.string().nullable(),
});
/**
 * Zod schema for nested employee in shift response
 */
const EmployeeInShiftSchema = z.object({
    id: z.uuid(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const ShiftMateBreakSchema = z.object({
    startTime: z.iso.datetime(),
    endTime: z.iso.datetime(),
    description: z.string().optional(),
});
export const ShiftMateSchema = z.object({
    shiftId: z.uuid(),
    preferredName: z.string().nullable(),
    firstName: z.string(),
    lastName: z.string(),
    jobRoleName: z.string(),
    startTime: z.iso.datetime(),
    endTime: z.iso.datetime(),
    breaks: z.array(ShiftMateBreakSchema),
});
export const ShiftMatesResponseSchema = z.object({
    shiftMates: z.array(ShiftMateSchema),
});
/**
 * Shift response schema for oRPC
 *
 * CURRENT STATE (v2 implementation):
 * - Returns nested jobRole, location, employee objects from Prisma include
 * - Includes v1 compatibility shim fields (isOpenShift, status, createdByUserId)
 *
 * FUTURE STATE (when refactored):
 * - Will return IDs only (jobRoleId, locationId, employeeId)
 * - Frontend will fetch relations separately
 * - Compatibility fields will be removed
 */
export const ShiftResponseSchema = z.object({
    id: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    startTime: z.iso.datetime(),
    endTime: z.iso.datetime(),
    jobRoleIds: z.array(z.uuid()),
    locationId: z.uuid(),
    employeeId: z.uuid().nullable(),
    companyId: z.uuid(),
    jobRoleFamilyId: z.uuid(),
    // New fields
    tipPooling: TipPoolingTypeSchema,
    notes: z.string(),
    allowEarlyClockIn: z.boolean().optional(),
    isUnplanned: z.boolean(),
    // Draft Fields
    status: ShiftStatusSchema,
    editedBy: z.uuid().nullable(),
    parentShiftId: z.uuid().nullable(),
    // Nested relations (current reality)
    jobRoles: z.array(JobRoleInShiftSchema),
    jobRoleFamily: z.object({
        id: z.uuid(),
        name: z.string(),
    }).optional(), // Job role family - used for display when jobRoles is empty
    location: LocationInShiftSchema,
    employee: EmployeeInShiftSchema.nullable(),
    // Computed/aggregated fields
    breaks: z.array(z.any()), // BreakResponse[] - using z.any() to avoid circular dependency
    violations: z.array(ViolationSchema),
    timezone: z.string().nullable(),
    skills: z.array(z.any()), // BaseSkillResponse[] - using z.any() to avoid circular dependency
    // V1 compatibility shim fields (will be removed)
    storeLocation: LocationInShiftSchema.optional(),
    storeLocationId: z.uuid().optional(),
    isOpenShift: z.boolean().optional(),
    createdByUserId: z.uuid().optional(),
    // Effective pay configuration
    effectivePayConfiguration: z.any().optional(), // PayConfigurationResponse - using z.any() to avoid circular dependency
});
/**
 * ShiftListItemSchema - Simplified shift response for list endpoints
 *
 * Used by list endpoints that return basic Shift[] without relations.
 * Contains only the core fields from the Shift model, with dates as ISO strings.
 */
export const ShiftListItemSchema = z.object({
    id: z.uuid(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    startTime: z.iso.datetime(),
    endTime: z.iso.datetime(),
    jobRoleIds: z.array(z.uuid()),
    locationId: z.uuid(),
    employeeId: z.uuid().nullable(),
    companyId: z.uuid(),
    jobRoleFamilyId: z.uuid().nullable(),
    tipPooling: TipPoolingTypeSchema,
    notes: z.string().nullable(),
    status: ShiftStatusSchema,
    editedBy: z.uuid().nullable(),
    parentShiftId: z.uuid().nullable(),
});
/**
 * Extended shift response for "my shifts" endpoint
 * Includes timecard status to allow the frontend to determine
 * whether the shift can be clocked into
 */
export const MyShiftResponseSchema = ShiftResponseSchema.extend({
    timecardId: z.uuid().nullable(),
    timecardStatus: TimecardStatusSchema.nullable(),
});
/**
 * Batch publish response schema (oRPC)
 * Returns results of batch operations with created/updated/deleted shifts
 */
export const BatchPublishResponseSchema = z.object({
    created: z.array(ShiftResponseSchema),
    updated: z.array(ShiftResponseSchema),
    deleted: z.array(z.uuid()), // Deleted shift IDs
    assigned: z.array(ShiftResponseSchema),
    errors: z.array(z.string()), // Error messages for failed operations
    publishEventId: z.uuid(), // ID of the publish event record
});
// ===== RETROACTIVE SHIFT TYPES =====
export const CreateRetroactiveShiftInputSchema = z.object({
    startTime: z.iso.datetime(),
    endTime: z.iso.datetime(),
    jobRoleIds: z.array(z.uuid()).default([]),
    jobRoleFamilyId: z.uuid(),
    locationId: z.uuid(),
    employeeId: z.uuid(),
    tipPooling: TipPoolingTypeSchema,
    notes: z.string().max(1000).default(""),
    payConfiguration: CreatePayConfigurationInputSchema.optional(),
    breaks: z.array(CreateBreakForShiftInputSchema).optional(),
    clockIn: z.iso.datetime().optional(),
    clockOut: z.iso.datetime().optional(),
    timecardBreaks: z.array(z.object({
        clockIn: z.iso.datetime(),
        clockOut: z.iso.datetime(),
        breakType: BreakTypeSchema,
    })).optional().default([]),
}).refine((data) => new Date(data.endTime) > new Date(data.startTime), { message: "End time must be after start time", path: ["endTime"] });
export const RetroactiveShiftResponseSchema = z.object({
    shift: ShiftResponseSchema,
    timecard: TimecardResponseSchema,
});
